import React, { FC, useEffect, useState } from "react";
import { CircledMenu } from "../Icon/icons/CircledMenu";
import { Trans } from "@helpers/translate";
import { Video } from "../Video";
import ModalAllImages from "./ModalAllImages";
import { Image } from "@components/ui/Image";
import { IGalleryItemProps } from ".";

export type GalleryProps = {
  gallery: { md: IGalleryItemProps[]; original: IGalleryItemProps[] };
};

export const GalleryCollage: FC<GalleryProps> = ({ gallery }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [client, setClient] = useState(false);

  const handleNavigateToImage = () => {
    setModalVisible(true);
  };

  // Remove supplier page scroll bar when gallery is open
  useEffect(() => {
    const body = document.body as HTMLElement;

    const originalOverflow = body.style.overflow;
    const originalPosition = body.style.position;
    const originalWidth = body.style.width;

    if (modalVisible) {
      // Disable scrolling when modal is open
      body.style.overflow = "hidden";
      body.style.position = "fixed";
      body.style.width = "100%";
    }

    //restore original styles when model closed or component unmounted
    return () => {
      body.style.overflow = originalOverflow || "auto";
      body.style.position = originalPosition || "static";
      body.style.width = originalWidth || "";
    };
  }, [modalVisible]);

  // When gallery is closed, scroll to top of supplier page smoothly
  useEffect(() => {
    if (!modalVisible) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    setClient(true);
  }, [modalVisible]);

  return (
    <div className="flex">
      <div className="flex flex-row relative w-full h-40-vw max-h-50-vh">
        {gallery.md[0].type === "image" ? (
          <button
            className="flex relative w-1/2 h-full mr-2 hover:opacity-75 rounded-2xl"
            onClick={handleNavigateToImage}
          >
            <Image
              src={gallery.md[0].src}
              alt={gallery.md[0].alt || ""}
              className="rounded-xl object-cover w-full h-full border"
              fill={true}
              activeCache={true}
            />
          </button>
        ) : (
          <button
            className="flex relative w-1/2 h-full mr-2 hover:opacity-75 bg-black rounded-2xl"
            onClick={handleNavigateToImage}
          >
            <div className="flex w-full h-full my-auto">
              {!modalVisible && client && (
                <Video
                  src={gallery.md[0].src}
                  thumbnail={gallery.md[0].thumbnail}
                  className="rounded-2xl"
                />
              )}
            </div>
          </button>
        )}

        {gallery.md.length === 2 && (
          <div className="flex w-1/2">
            <button
              className="flex relative w-full h-full hover:opacity-75"
              onClick={handleNavigateToImage}
            >
              <Image
                src={gallery.md[1].src}
                alt={
                  gallery.md[1].type === "image" ? gallery.md[1].alt || "" : ""
                }
                className="object-cover w-full h-full border rounded-xl"
                fill={true}
                activeCache={true}
              />
            </button>
          </div>
        )}
        {(gallery.md.length === 3 || gallery.md.length === 4) && (
          <div className="grid grid-cols-1 grid-rows-2 gap-2 w-1/2">
            <button
              className="flex relative w-full h-full hover:opacity-75"
              onClick={handleNavigateToImage}
            >
              <Image
                src={gallery.md[1].src}
                alt={
                  gallery.md[1].type === "image" ? gallery.md[1].alt || "" : ""
                }
                className="object-cover w-full h-full border rounded-xl"
                fill={true}
                activeCache={true}
              />
            </button>
            <button
              className="flex relative w-full h-full hover:opacity-75"
              onClick={handleNavigateToImage}
            >
              <Image
                src={gallery.md[2].src}
                alt={
                  gallery.md[2].type === "image" ? gallery.md[2].alt || "" : ""
                }
                className="object-cover w-full h-full border rounded-xl"
                fill={true}
                activeCache={true}
              />
            </button>
          </div>
        )}

        {gallery.md.length >= 5 && (
          <div className="grid grid-cols-2 grid-rows-2 gap-2 w-1/2">
            <button
              className="flex relative w-full h-full hover:opacity-75"
              onClick={handleNavigateToImage}
            >
              <Image
                src={gallery.md[1].src}
                alt={
                  gallery.md[1].type === "image" ? gallery.md[1].alt || "" : ""
                }
                className="object-cover w-full h-full border rounded-xl"
                fill={true}
                activeCache={true}
              />
            </button>
            <button
              className="flex relative w-full h-full hover:opacity-75"
              onClick={handleNavigateToImage}
            >
              <Image
                src={gallery.md[2].src}
                alt={
                  gallery.md[2].type === "image" ? gallery.md[2].alt || "" : ""
                }
                className="object-cover w-full h-full border rounded-xl"
                fill={true}
                activeCache={true}
              />
            </button>
            <button
              className="flex relative w-full h-full hover:opacity-75"
              onClick={handleNavigateToImage}
            >
              <Image
                src={gallery.md[3].src}
                alt={
                  gallery.md[3].type === "image" ? gallery.md[3].alt || "" : ""
                }
                className="object-cover w-full h-full border rounded-xl"
                fill={true}
                activeCache={true}
              />
            </button>
            <button
              className="flex relative w-full h-full hover:opacity-75"
              onClick={handleNavigateToImage}
            >
              <Image
                src={gallery.md[4].src}
                alt={
                  gallery.md[4].type === "image" ? gallery.md[4].alt || "" : ""
                }
                className="object-cover w-full h-full border rounded-xl"
                fill={true}
                activeCache={true}
              />
            </button>
          </div>
        )}

        {gallery.md.length >= 2 && (
          <div className="absolute bottom-4 right-4">
            <button
              className="flex bg-gray-200 px-3 py-1 rounded-lg border-gray-600 items-center hover:bg-white"
              onClick={() => setModalVisible(true)}
            >
              <CircledMenu className="w-5 h-5" fill="text-gray-700 border" />
              <p className="whitespace-nowrap ml-1 text-gray-700">
                <Trans id="tour.gallery.show-all-photos">
                  Show all {gallery.md.length} photos
                </Trans>
              </p>
            </button>
          </div>
        )}
      </div>

      <ModalAllImages
        visible={modalVisible}
        gallery={gallery}
        onRequestClose={() => setModalVisible(false)}
      />
    </div>
  );
};
